import {
  Badge,
  Box,
  Button,
  Container,
  createToaster,
  Flex,
  Heading,
  HStack,
  Icon,
  Tabs,
  Text,
  VStack
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";
import { useRef, useState } from "react";
import { 
  FiPlus,
  FiCode,
  FiLink,
  FiGlobe,
  FiZap
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { mockApi } from "../api/mockApi";
import {
  ABTestsTable,
  BulkActionsMenu,
  EmptyState,
  ErrorState,
  LoadingState,
  PathsTable,
  PreconnectTable
} from "../components/Website";

// Import AG Grid Enterprise and register modules
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {
  AllEnterpriseModule,
  IntegratedChartsModule
} from "ag-grid-enterprise";

// Register AG Grid modules including charts
ModuleRegistry.registerModules([
  AllCommunityModule,
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);

// Create toaster instance
const toast = createToaster({
  placement: "top",
  pauseOnPageIdle: true
});

function Website() {
  const gridRef = useRef<AgGridReact>(null);
  const pathGridRef = useRef<AgGridReact>(null);
  const scriptGridRef = useRef<AgGridReact>(null);
  const preconnectGridRef = useRef<AgGridReact>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedPaths, setSelectedPaths] = useState<any[]>([]);
  const [selectedScripts, setSelectedScripts] = useState<any[]>([]);
  const [selectedPreconnects, setSelectedPreconnects] = useState<any[]>([]);
  const [loadingTests, setLoadingTests] = useState<Set<string>>(new Set());
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());
  const [loadingScripts, setLoadingScripts] = useState<Set<string>>(new Set());
  const [loadingPreconnects, setLoadingPreconnects] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState("paths");
  const queryClient = useQueryClient();

  const {
    data: abTests = [],
    isLoading: isLoadingTests,
    error: abTestsError,
    refetch: refetchTests
  } = useQuery({
    queryKey: ["abTests"],
    queryFn: mockApi.getABTests
  });

  const {
    data: paths = [],
    isLoading: isLoadingPaths,
    error: pathsError,
    refetch: refetchPaths
  } = useQuery({
    queryKey: ["paths"],
    queryFn: mockApi.getPaths
  });

  const {
    data: scripts = [],
    isLoading: isLoadingScripts,
    error: scriptsError,
    refetch: refetchScripts
  } = useQuery({
    queryKey: ["scripts"],
    queryFn: mockApi.getScripts
  });

  const {
    data: preconnectEntries = [],
    isLoading: isLoadingPreconnects,
    error: preconnectsError,
    refetch: refetchPreconnects
  } = useQuery({
    queryKey: ["preconnectEntries"],
    queryFn: mockApi.getPreconnectEntries
  });


  // Mutation for updating test status
  const toggleStatusMutation = useMutation({
    mutationFn: async ({
      id,
      newStatus
    }: {
      id: string;
      newStatus: string;
    }) => {
      return mockApi.updateABTest(id, { status: newStatus as any });
    },
    onMutate: (variables) => {
      setLoadingTests((prev) => new Set(prev).add(variables.id));
    },
    onSuccess: (_, variables) => {
      setLoadingTests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });

      queryClient.invalidateQueries({ queryKey: ["abTests"] });
      const isActivating = variables.newStatus === "active";
      toast.create({
        title: isActivating ? "Test Activated" : "Test Paused",
        description: isActivating
          ? "The A/B test is now active and collecting data."
          : "The A/B test has been paused.",
        type: "success",
        duration: 3000
      });
    },
    onError: (error: any, variables) => {
      setLoadingTests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });

      const isActivating = variables.newStatus === "active";
      toast.create({
        title: `Failed to ${isActivating ? "activate" : "pause"} test`,
        description:
          error.message || "An error occurred while updating the test status",
        type: "error",
        duration: 5000
      });
    }
  });

  // Mutation for updating path status
  const togglePathStatusMutation = useMutation({
    mutationFn: async ({
      id,
      newStatus
    }: {
      id: string;
      newStatus: string;
    }) => {
      return mockApi.updatePath(id, { status: newStatus as any });
    },
    onMutate: (variables) => {
      setLoadingPaths((prev) => new Set(prev).add(variables.id));
    },
    onSuccess: (_, variables) => {
      setLoadingPaths((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });

      queryClient.invalidateQueries({ queryKey: ["paths"] });
      const isActivating = variables.newStatus === "active";
      toast.create({
        title: isActivating ? "Path Activated" : "Path Deactivated",
        description: isActivating
          ? "The path is now active and accepting requests."
          : "The path has been deactivated.",
        type: "success",
        duration: 3000
      });
    },
    onError: (error: any, variables) => {
      setLoadingPaths((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });

      const isActivating = variables.newStatus === "active";
      toast.create({
        title: `Failed to ${isActivating ? "activate" : "deactivate"} path`,
        description:
          error.message || "An error occurred while updating the path status",
        type: "error",
        duration: 5000
      });
    }
  });

  // Mutation for updating script status
  const toggleScriptStatusMutation = useMutation({
    mutationFn: async ({
      id,
      newStatus
    }: {
      id: string;
      newStatus: string;
    }) => {
      return mockApi.updateScript(id, { status: newStatus as any });
    },
    onMutate: (variables) => {
      setLoadingScripts((prev) => new Set(prev).add(variables.id));
    },
    onSuccess: (_, variables) => {
      setLoadingScripts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });

      queryClient.invalidateQueries({ queryKey: ["scripts"] });
      const isActivating = variables.newStatus === "active";
      toast.create({
        title: isActivating ? "Script Activated" : "Script Deactivated",
        description: isActivating
          ? "The script template is now active."
          : "The script template has been deactivated.",
        type: "success",
        duration: 3000
      });
    },
    onError: (error: any, variables) => {
      setLoadingScripts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });

      const isActivating = variables.newStatus === "active";
      toast.create({
        title: `Failed to ${isActivating ? "activate" : "deactivate"} script`,
        description:
          error.message || "An error occurred while updating the script status",
        type: "error",
        duration: 5000
      });
    }
  });

  // Mutation for updating preconnect entry status
  const togglePreconnectStatusMutation = useMutation({
    mutationFn: async ({
      id,
      newStatus
    }: {
      id: string;
      newStatus: string;
    }) => {
      return mockApi.updatePreconnectEntry(id, { status: newStatus as any });
    },
    onMutate: (variables) => {
      setLoadingPreconnects((prev) => new Set(prev).add(variables.id));
    },
    onSuccess: (_, variables) => {
      setLoadingPreconnects((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });

      queryClient.invalidateQueries({ queryKey: ["preconnectEntries"] });
      const isActivating = variables.newStatus === "active";
      toast.create({
        title: isActivating ? "Preconnect Entry Activated" : "Preconnect Entry Deactivated",
        description: isActivating
          ? "The preconnect entry is now active."
          : "The preconnect entry has been deactivated.",
        type: "success",
        duration: 3000
      });
    },
    onError: (error: any, variables) => {
      setLoadingPreconnects((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });

      const isActivating = variables.newStatus === "active";
      toast.create({
        title: `Failed to ${isActivating ? "activate" : "deactivate"} preconnect entry`,
        description:
          error.message || "An error occurred while updating the preconnect entry status",
        type: "error",
        duration: 5000
      });
    }
  });

  // Handle status toggle
  const handleToggleStatus = (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "paused" : "active";
    toggleStatusMutation.mutate({ id, newStatus });
  };

  const handleTogglePathStatus = (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    togglePathStatusMutation.mutate({ id, newStatus });
  };

  const handleToggleScriptStatus = (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    toggleScriptStatusMutation.mutate({ id, newStatus });
  };

  const handleTogglePreconnectStatus = (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    togglePreconnectStatusMutation.mutate({ id, newStatus });
  };

  // Handle bulk actions for A/B tests
  const handleBulkEdit = () => {
    console.log("Bulk edit", selectedRows);
  };

  const handleBulkPause = () => {
    console.log("Bulk pause", selectedRows);
  };

  // Handle bulk actions for paths
  const handleBulkEditPaths = () => {
    console.log("Bulk edit paths", selectedPaths);
  };

  const handleBulkDeactivatePaths = () => {
    console.log("Bulk deactivate paths", selectedPaths);
  };

  const isLoading = isLoadingTests || isLoadingPaths || isLoadingScripts || isLoadingPreconnects;
  const error = abTestsError || pathsError || scriptsError || preconnectsError;

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error as Error} refetch={() => {
      refetchTests();
      refetchPaths();
      refetchScripts();
      refetchPreconnects();
    }} />;
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="7xl" py={8}>
        <VStack gap={8} align="stretch">
          {/* Header Section */}
          <Box>
            <Heading size="2xl" mb={2}>
              Website
            </Heading>
            <Text color="gray.600">
              Manage paths, A/B tests, and script templates
            </Text>
          </Box>


          {/* Main Content */}
          <Box
            bg="white"
            rounded="2xl"
            shadow="xl"
            borderWidth="1px"
            borderColor="gray.200"
            overflow="hidden"
          >
            <Tabs.Root
              value={activeTab}
              onValueChange={(e) => setActiveTab(e.value)}
            >
              <Box borderBottom="1px" borderColor="gray.200">
                <Tabs.List px={6}>
                  <Tabs.Trigger value="paths" py={4} px={4}>
                    <HStack>
                      <Icon as={FiGlobe} />
                      <Text>Paths</Text>
                      {paths.length > 0 && (
                        <Badge size="sm" colorScheme="gray">
                          {paths.length}
                        </Badge>
                      )}
                    </HStack>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="ab-testing" py={4} px={4}>
                    <HStack>
                      <Icon as={FiZap} />
                      <Text>A/B Testing</Text>
                      {abTests.length > 0 && (
                        <Badge size="sm" colorScheme="gray">
                          {abTests.length}
                        </Badge>
                      )}
                    </HStack>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="scripts" py={4} px={4}>
                    <HStack>
                      <Icon as={FiCode} />
                      <Text>Scripts</Text>
                      {scripts.length > 0 && (
                        <Badge size="sm" colorScheme="gray">
                          {scripts.length}
                        </Badge>
                      )}
                    </HStack>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="preconnect" py={4} px={4}>
                    <HStack>
                      <Icon as={FiLink} />
                      <Text>Preconnect</Text>
                      {preconnectEntries.length > 0 && (
                        <Badge size="sm" colorScheme="gray">
                          {preconnectEntries.length}
                        </Badge>
                      )}
                    </HStack>
                  </Tabs.Trigger>
                </Tabs.List>
              </Box>

              {/* Paths Tab */}
              <Tabs.Content value="paths">
                <Box p={6}>
                  <Flex justify="space-between" align="center" mb={6}>
                    <Box />
                    <HStack gap={3}>
                      {selectedPaths.length > 0 && (
                        <BulkActionsMenu
                          selectedCount={selectedPaths.length}
                          onBulkEdit={handleBulkEditPaths}
                          onBulkPause={handleBulkDeactivatePaths}
                        />
                      )}
                      <Link to="/website/path/create">
                        <Button
                          size="lg"
                          colorScheme="blue"
                          _hover={{ transform: "translateY(-2px)" }}
                        >
                          <FiPlus style={{ marginRight: "8px" }} />
                          Create Path
                        </Button>
                      </Link>
                    </HStack>
                  </Flex>

                  {paths.length === 0 ? (
                    <VStack py={16} gap={4}>
                      <Icon as={FiGlobe} boxSize={12} color="gray.300" />
                      <Heading size="md">No paths configured</Heading>
                      <Text color="gray.600" textAlign="center" maxW="md">
                        Create custom URL paths to redirect visitors and track campaigns
                      </Text>
                      <Link to="/website/path/create">
                        <Button colorScheme="blue">
                          <FiPlus style={{ marginRight: "8px" }} />
                          Create Your First Path
                        </Button>
                      </Link>
                    </VStack>
                  ) : (
                    <PathsTable
                      ref={pathGridRef}
                      paths={paths}
                      onSelectionChanged={setSelectedPaths}
                      onToggleStatus={handleTogglePathStatus}
                      loadingPaths={loadingPaths}
                    />
                  )}
                </Box>
              </Tabs.Content>

              {/* A/B Testing Tab */}
              <Tabs.Content value="ab-testing">
                <Box p={6}>
                  <Flex justify="space-between" align="center" mb={6}>
                    <Box />
                    <HStack gap={3}>
                      {selectedRows.length > 0 && (
                        <BulkActionsMenu
                          selectedCount={selectedRows.length}
                          onBulkEdit={handleBulkEdit}
                          onBulkPause={handleBulkPause}
                        />
                      )}
                      <Link to="/website/ab-test/create">
                        <Button
                          size="lg"
                          colorScheme="blue"
                          _hover={{ transform: "translateY(-2px)" }}
                        >
                          <FiPlus style={{ marginRight: "8px" }} />
                          Create A/B Test
                        </Button>
                      </Link>
                    </HStack>
                  </Flex>

                  {abTests.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <ABTestsTable
                      ref={gridRef}
                      abTests={abTests}
                      onSelectionChanged={setSelectedRows}
                      onToggleStatus={handleToggleStatus}
                      loadingTests={loadingTests}
                    />
                  )}
                </Box>
              </Tabs.Content>

              {/* Scripts Tab */}
              <Tabs.Content value="scripts">
                <Box p={6}>
                  <Flex justify="space-between" align="center" mb={6}>
                    <Box />
                    <HStack gap={3}>
                      {selectedScripts.length > 0 && (
                        <BulkActionsMenu
                          selectedCount={selectedScripts.length}
                          onBulkEdit={() => console.log("Bulk edit scripts")}
                          onBulkPause={() => console.log("Bulk deactivate scripts")}
                        />
                      )}
                      <Link to="/website/script/create">
                        <Button
                          size="lg"
                          colorScheme="blue"
                          _hover={{ transform: "translateY(-2px)" }}
                        >
                          <FiPlus style={{ marginRight: "8px" }} />
                          Create Script
                        </Button>
                      </Link>
                    </HStack>
                  </Flex>

                  {scripts.length === 0 ? (
                    <VStack py={16} gap={4}>
                      <Icon as={FiCode} boxSize={12} color="gray.300" />
                      <Heading size="md">No script templates</Heading>
                      <Text color="gray.600" textAlign="center" maxW="md">
                        Create reusable script templates for tracking and customization
                      </Text>
                      <Link to="/website/script/create">
                        <Button colorScheme="blue">
                          <FiPlus style={{ marginRight: "8px" }} />
                          Create Your First Script
                        </Button>
                      </Link>
                    </VStack>
                  ) : (
                    <ScriptsTableAG
                      ref={scriptGridRef}
                      scripts={scripts}
                      onSelectionChanged={setSelectedScripts}
                      onToggleStatus={handleToggleScriptStatus}
                      loadingScripts={loadingScripts}
                    />
                  )}
                </Box>
              </Tabs.Content>

              {/* Preconnect Tab */}
              <Tabs.Content value="preconnect">
                <Box p={6}>
                  <Flex justify="space-between" align="center" mb={6}>
                    <Box />
                    <HStack gap={3}>
                      {selectedPreconnects.length > 0 && (
                        <BulkActionsMenu
                          selectedCount={selectedPreconnects.length}
                          onBulkEdit={() => console.log("Bulk edit preconnects")}
                          onBulkPause={() => console.log("Bulk deactivate preconnects")}
                        />
                      )}
                      <Link to="/website/preconnect/create">
                        <Button
                          size="lg"
                          colorScheme="blue"
                          _hover={{ transform: "translateY(-2px)" }}
                        >
                          <FiPlus style={{ marginRight: "8px" }} />
                          Add Preconnect
                        </Button>
                      </Link>
                    </HStack>
                  </Flex>

                  {preconnectEntries.length === 0 ? (
                    <VStack py={16} gap={4}>
                      <Icon as={FiLink} boxSize={12} color="gray.300" />
                      <Heading size="md">No preconnect entries</Heading>
                      <Text color="gray.600" textAlign="center" maxW="md">
                        Add DNS prefetch and preconnect hints to improve page load performance
                      </Text>
                      <Link to="/website/preconnect/create">
                        <Button colorScheme="blue">
                          <FiPlus style={{ marginRight: "8px" }} />
                          Add Your First Preconnect
                        </Button>
                      </Link>
                    </VStack>
                  ) : (
                    <PreconnectTable
                      ref={preconnectGridRef}
                      entries={preconnectEntries}
                      onSelectionChanged={setSelectedPreconnects}
                      onToggleStatus={handleTogglePreconnectStatus}
                      loadingEntries={loadingPreconnects}
                    />
                  )}
                </Box>
              </Tabs.Content>
            </Tabs.Root>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}

// Scripts table using AG Grid
function ScriptsTableAG({ scripts, onSelectionChanged, onToggleStatus: _onToggleStatus, loadingScripts: _loadingScripts }: any) {
  const gridRef = useRef<AgGridReact>(null);

  const columnDefs = [
    {
      field: "name",
      headerName: "Template Name",
      flex: 1.5,
      minWidth: 200
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 250
    },
    {
      field: "placement",
      headerName: "Placement",
      width: 120,
      cellRenderer: (params: any) => {
        return params.value?.toUpperCase() || "";
      }
    },
    {
      field: "type",
      headerName: "Type",
      width: 120,
      cellRenderer: (params: any) => {
        const type = params.value;
        return type ? type.charAt(0).toUpperCase() + type.slice(1) : "";
      }
    },
    {
      field: "usageCount",
      headerName: "Usage",
      width: 100,
      cellRenderer: (params: any) => {
        const count = params.value || 0;
        return `${count} ${count === 1 ? "path" : "paths"}`;
      }
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      cellRenderer: (params: any) => {
        const { status } = params.data;
        const isActive = status === "active";
        return isActive ? "Active" : "Inactive";
      }
    },
    {
      field: "lastModified",
      headerName: "Last Modified",
      width: 150
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 120
    }
  ];

  return (
    <Box className="ag-theme-alpine" height="600px">
      <AgGridReact
        ref={gridRef}
        rowData={scripts}
        columnDefs={columnDefs}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true
        }}
        rowSelection={{
          mode: "multiRow",
          enableClickSelection: false,
          checkboxes: true,
          headerCheckbox: true
        }}
        onSelectionChanged={() => {
          const selectedNodes = gridRef.current?.api.getSelectedNodes();
          const selectedData = selectedNodes?.map((node) => node.data) || [];
          onSelectionChanged(selectedData);
        }}
        pagination={true}
        paginationPageSize={10}
        paginationPageSizeSelector={[10, 20, 50, 100]}
        rowHeight={60}
        animateRows={true}
      />
    </Box>
  );
}

export default Website;